/**
 *    SPDX-License-Identifier: Apache-2.0
 */

import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import FontAwesome from 'react-fontawesome';
import JSONTree from 'react-json-tree';
import { transactionType } from '../types';

const styles = () => ({
	container: {
		minHeight: '100vh',
		backgroundColor: '#f5f5f5',
		padding: 0,
		margin: 0,
		marginBottom: 100,
		paddingBottom: 20,
		fontFamily: 'Arial, sans-serif'
	},
	header: {
		backgroundColor: '#4A90E2',
		color: '#ffffff',
		padding: '20px 40px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
	},
	headerLeft: {
		display: 'flex',
		alignItems: 'center',
		'& > *': {
			marginRight: '15px',
			'&:last-child': {
				marginRight: 0
			}
		}
	},
	logo: {
		fontSize: '24px',
		fontWeight: 'bold'
	},
	headerCenter: {
		display: 'flex',
		alignItems: 'center',
		'& > *': {
			marginRight: '10px',
			'&:last-child': {
				marginRight: 0
			}
		},
		fontSize: '20px',
		fontWeight: 'bold'
	},
	headerRight: {
		'& a': {
			color: '#ffffff',
			textDecoration: 'none',
			fontSize: '16px',
			'&:hover': {
				textDecoration: 'underline'
			}
		}
	},
	mainContent: {
		maxWidth: '1200px',
		margin: '40px auto',
		padding: '0 20px'
	},
	contentCard: {
		backgroundColor: '#ffffff',
		borderRadius: '8px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
		padding: '30px'
	},
	title: {
		fontSize: '24px',
		fontWeight: 'bold',
		marginBottom: '30px',
		color: '#333333',
		borderBottom: '2px solid #4A90E2',
		paddingBottom: '10px'
	},
	dataRow: {
		display: 'flex',
		padding: '15px 0',
		borderBottom: '1px solid #e0e0e0',
		'&:last-child': {
			borderBottom: 'none'
		}
	},
	label: {
		fontWeight: 'bold',
		width: '200px',
		color: '#666666',
		flexShrink: 0
	},
	value: {
		flex: 1,
		color: '#333333',
		wordBreak: 'break-word'
	},
	status: {
		color: '#28a745',
		fontWeight: 'bold'
	},
	blockLink: {
		color: '#4A90E2',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline'
		}
	},
	copyButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		marginLeft: '10px',
		color: '#666666',
		'&:hover': {
			color: '#4A90E2'
		}
	},
	dataSection: {
		marginTop: '30px',
		paddingTop: '30px',
		borderTop: '2px solid #e0e0e0'
	},
	dataHash: {
		fontFamily: 'monospace',
		fontSize: '14px',
		color: '#333333',
		marginBottom: '20px',
		wordBreak: 'break-all'
	},
	jsonContainer: {
		backgroundColor: '#f9f9f9',
		border: '1px solid #e0e0e0',
		borderRadius: '4px',
		padding: '15px',
		overflow: 'auto',
		maxHeight: '600px'
	},
	loading: {
		textAlign: 'center',
		padding: '40px',
		fontSize: '18px',
		color: '#666666'
	},
	error: {
		textAlign: 'center',
		padding: '40px',
		fontSize: '18px',
		color: '#dc3545'
	}
});

const jsonTheme = {
	base00: '#f9f9f9',
	base01: '#2e2f30',
	base02: '#515253',
	base03: '#737475',
	base04: '#959697',
	base05: '#b7b8b9',
	base06: '#dadbdc',
	base07: '#fcfdfe',
	base08: '#e31a1c',
	base09: '#e6550d',
	base0A: '#dca060',
	base0B: '#31a354',
	base0C: '#80b1d3',
	base0D: '#3182bd',
	base0E: '#756bb1',
	base0F: '#b15928'
};

export class TransactionDetailPage extends Component {
	componentDidMount() {
		const { match, getTransaction, transaction } = this.props;
		const transId = match && match.params && match.params.transId;
		
		// Fetch transaction if not already loaded or different ID
		if (transId && getTransaction && (!transaction || transaction.txhash !== transId)) {
			getTransaction('ChannelNotSpecified', transId);
		}
	}

	componentDidUpdate(prevProps) {
		const { match, getTransaction, transaction } = this.props;
		const transId = match && match.params && match.params.transId;
		const prevTransId = prevProps.match && prevProps.match.params && prevProps.match.params.transId;
		
		// Fetch transaction if transId changed
		if (transId && transId !== prevTransId && getTransaction) {
			getTransaction('ChannelNotSpecified', transId);
		}
	}

	formatTimestamp = timestamp => {
		if (!timestamp) return '-';
		const date = new Date(timestamp);
		return date.toLocaleString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false
		}).replace(',', '');
	};

	handleBlockClick = (e, blockNum) => {
		e.preventDefault();
		if (blockNum && blockNum !== '-') {
			const { history } = this.props;
			if (history) {
				history.push(`/blocks/${blockNum}`);
			}
		}
	};

	getWriteDataObject = transaction => {
		if (!transaction || !Array.isArray(transaction.write_set)) {
			return null;
		}

		const result = {};

		transaction.write_set.forEach(writeEntry => {
			if (!writeEntry || !Array.isArray(writeEntry.set)) {
				return;
			}

			writeEntry.set.forEach(item => {
				if (!item) {
					return;
				}

				const key = item.key;
				let value = item.value;

				if (typeof value === 'string') {
					const trimmed = value.trim();
					if (
						(trimmed.startsWith('{') && trimmed.endsWith('}')) ||
						(trimmed.startsWith('[') && trimmed.endsWith(']'))
					) {
						try {
							value = JSON.parse(trimmed);
						} catch (e) {
							// keep original string if JSON.parse fails
						}
					}
				}

				if (key) {
					result[key] = {
						value,
						is_delete: Boolean(item.is_delete)
					};
				}
			});
		});

		return Object.keys(result).length ? result : null;
	};

	render() {
		const { transaction, classes, currentChannel } = this.props;

		if (!transaction || !transaction.txhash) {
			return (
				<div className={classes.container}>
					<div className={classes.header}>
						<div className={classes.headerLeft}>
							<div className={classes.logo}>Hyperledger Explorer</div>
						</div>
						<div className={classes.headerCenter}>
							<span>Transaction Details</span>
						</div>
						<div className={classes.headerRight}>
							<a href="/">HOME</a>
						</div>
					</div>
					<div className={classes.loading}>
						<FontAwesome name="circle-o-notch" size="2x" spin /> Loading transaction...
					</div>
				</div>
			);
		}

		if (transaction.error) {
			return (
				<div className={classes.container}>
					<div className={classes.header}>
						<div className={classes.headerLeft}>
							<div className={classes.logo}>Hyperledger Explorer</div>
						</div>
						<div className={classes.headerCenter}>
							<span>Transaction Details</span>
						</div>
						<div className={classes.headerRight}>
							<a href="/">HOME</a>
						</div>
					</div>
					<div className={classes.error}>
						Error loading transaction: {transaction.error}
					</div>
				</div>
			);
		}

		// Extract block number if available
		const blockNum = transaction.blocknum || '-';
		const writeDataObject = this.getWriteDataObject(transaction);

		return (
			<div className={classes.container}>
				<div className={classes.header}>
					<div className={classes.headerLeft}>
						<div className={classes.logo}>Hyperledger Explorer</div>
					</div>
					<div className={classes.headerCenter}>
						<span>Transaction Details</span>
					</div>
					<div className={classes.headerRight}>
						<a
							href="#"
							onClick={e => {
								e.preventDefault();
								const { history } = this.props;
								if (history) {
									history.push('/');
								}
							}}
						>
							HOME
						</a>
					</div>
				</div>

				<div className={classes.mainContent}>
					<div className={classes.contentCard}>
						<div className={classes.title}>Transaction data</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Tx Hash:</div>
							<div className={classes.value}>
								{transaction.txhash}
								<CopyToClipboard text={transaction.txhash}>
									<button className={classes.copyButton} type="button">
										<FontAwesome name="copy" />
									</button>
								</CopyToClipboard>
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Status:</div>
							<div className={`${classes.value} ${classes.status}`}>
								{transaction.validation_code || 'VALID'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Timestamp (UTC):</div>
							<div className={classes.value}>
								{this.formatTimestamp(transaction.createdt)}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Block:</div>
							<div className={classes.value}>
								{blockNum !== '-' ? (
									<a
										href={`/blocks/${blockNum}`}
										className={classes.blockLink}
										onClick={e => this.handleBlockClick(e, blockNum)}
									>
										{blockNum}
									</a>
								) : (
									'-'
								)}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Channel:</div>
							<div className={classes.value}>
								{transaction.channelname || currentChannel || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>ChainCode:</div>
							<div className={classes.value}>
								{transaction.chaincodename || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>From:</div>
							<div className={classes.value}>
								{transaction.creator_msp_id || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Endorser:</div>
							<div className={classes.value}>
								{transaction.endorser_msp_id || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Type:</div>
							<div className={classes.value}>
								{transaction.type || '-'}
							</div>
						</div>

						<div className={classes.dataRow}>
							<div className={classes.label}>Payload Proposal Hash:</div>
							<div className={classes.value}>
								{transaction.payload_proposal_hash || '-'}
								{transaction.payload_proposal_hash && (
									<CopyToClipboard text={transaction.payload_proposal_hash}>
										<button className={classes.copyButton} type="button">
											<FontAwesome name="copy" />
										</button>
									</CopyToClipboard>
								)}
							</div>
						</div>

						{writeDataObject && (
							<div className={classes.dataSection}>
								<div className={classes.title}>Data</div>
								<div className={classes.jsonContainer}>
									<JSONTree
										data={writeDataObject}
										theme={jsonTheme}
										invertTheme={false}
										shouldExpandNode={() => true}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

TransactionDetailPage.propTypes = {
	transaction: transactionType,
	classes: (props, propName) => {
		if (typeof props[propName] !== 'object') {
			return new Error(`${propName} must be an object`);
		}
	},
	history: (props, propName) => {
		if (props[propName] && typeof props[propName].push !== 'function') {
			return new Error(`${propName} must be a history object`);
		}
	},
	currentChannel: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'string') {
			return new Error(`${propName} must be a string`);
		}
	},
	match: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'object') {
			return new Error(`${propName} must be an object`);
		}
	},
	getTransaction: (props, propName) => {
		if (props[propName] && typeof props[propName] !== 'function') {
			return new Error(`${propName} must be a function`);
		}
	}
};

TransactionDetailPage.defaultProps = {
	transaction: null,
	history: null,
	currentChannel: '',
	match: null,
	getTransaction: null
};

export default withStyles(styles)(TransactionDetailPage);

